import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocations } from "src/hooks/useLocations";

interface LocationOption {
  value: string;
  label: string;
  isNew?: boolean;
}

interface LocationPickerProps {
  value: string | null;
  onChange: (location: string | null) => void;
  /** Let the user type a location that doesn't exist yet. */
  allowNew?: boolean;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  size?: "small" | "medium";
}

const filter = createFilterOptions<LocationOption>();

// The one location selector. Locations are free text on Product, so with
// allowNew a typed name that matches nothing becomes a new location, while a
// name that matches an existing location (ignoring case) selects that one
// rather than creating a near-duplicate like "gondola" beside "Gondola".
export const LocationPicker = (props: LocationPickerProps) => {
  const { value, onChange, allowNew } = props;
  const { locations } = useLocations();

  const options = useMemo(() => {
    const list: LocationOption[] = locations.map((l) => ({ value: l, label: l }));
    // Keep the current value selectable even if it isn't in the list yet
    // (a new location not saved yet, or one renamed since it was chosen).
    if (value && !locations.includes(value)) {
      list.push({ value, label: value });
    }
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [locations, value]);

  const selected = value ? options.find((o) => o.value === value) ?? null : null;

  // The input text is controlled so it follows `value`. Left to itself, MUI
  // keeps the typed text when a freeSolo Autocomplete's value is reset to
  // null (clearOnBlur is off), so a dialog that clears its location on reopen
  // would still show the previous choice with the action button disabled.
  const [inputValue, setInputValue] = useState(value ?? "");
  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const resolve = (text: string): string | null => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const existing = locations.find((l) => l.toLowerCase() === trimmed.toLowerCase());
    return existing ?? trimmed;
  };

  return (
    <Autocomplete<LocationOption, false, false, boolean>
      options={options}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_event, text) => setInputValue(text)}
      freeSolo={allowNew}
      autoSelect={allowNew}
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      fullWidth
      size={props.size}
      disabled={props.disabled}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const input = params.inputValue.trim();
        const exists = opts.some((o) => o.value.toLowerCase() === input.toLowerCase());
        if (allowNew && input && !exists) {
          filtered.push({ value: input, label: `Add "${input}"`, isNew: true });
        }
        return filtered;
      }}
      onChange={(_event, newValue) => {
        if (newValue === null) {
          onChange(null);
        } else if (typeof newValue === "string") {
          onChange(resolve(newValue));
        } else {
          onChange(newValue.isNew ? resolve(newValue.value) : newValue.value);
        }
      }}
      renderOption={(liProps, option) => {
        const { key, ...rest } = liProps as any;
        return (
          <li key={`${option.value}${option.isNew ? "-new" : ""}`} {...rest}>
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label || (props.required ? "Location *" : "Location")}
          error={props.error}
          helperText={props.helperText}
          autoFocus={props.autoFocus}
          sx={{ textAlign: "left" }}
        />
      )}
    />
  );
};
